// src/components/block/dashboardV2Managment/VideoFeatureButton.tsx
//
// Mise à la une du portail, pour une durée choisie (superadmin). Le choix s'ouvre dans une
// fenêtre posée sur le document : les lignes du tableau coupent ce qui déborde.

import React, { useState } from 'react';
import { createPortal } from 'react-dom';

import { setVideoFeatured } from '../../../backendServices/mediaManagment/api_video';

import styles from './ChannelsSection.module.scss';

import type { DashboardVideo } from './VideosSection';

const DURATIONS = [
  { value: '24'  , label: '24 heures' },
  { value: '72'  , label: '3 jours'   },
  { value: '168' , label: '7 jours'   },
  { value: '720' , label: '30 jours'  },
  { value: 'date', label: 'Jusqu\'à une date précise' },
];

const untilText = (iso: string) =>
  new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

const VideoFeatureButton: React.FC<{ v: DashboardVideo; adminId: number; onChanged: () => void }> = ({ v, adminId, onChanged }) => {
  const [ open    , setOpen    ] = useState(false);
  const [ duration, setDuration] = useState('168');
  const [ date    , setDate    ] = useState('');
  const [ busy    , setBusy    ] = useState(false);
  const [ error   , setError   ] = useState('');

  const send = async (until: string | null) => {
    setBusy(true);
    setError('');
    const res = await setVideoFeatured(adminId, v.id, until);
    setBusy(false);
    if (!res.ok) { setError(res.error); return false; }
    setOpen(false);
    onChanged();
    return true;
  };

  const confirm = () => {
    if (duration === 'date') {
      if (!date) { setError('Choisissez une date de fin.'); return; }
      send(new Date(date).toISOString());
      return;
    }
    send(new Date(Date.now() + Number(duration) * 3600000).toISOString());
  };

  if (v.featuredUntil) {
    return (
      <>
        <button type="button" className={styles.secondary} disabled={busy} onClick={() => send(null)}
                title={`À la une jusqu'au ${untilText(v.featuredUntil)}`}>
          Retirer de la une
        </button>
        {error && <span role="alert">{error}</span>}
      </>
    );
  }

  const visible = v.active && v.publicPortal;

  return (
    <>
      <button type="button" className={styles.secondary} disabled={!visible} onClick={() => { setError(''); setOpen(true); }}
              title={visible ? 'Placer en tête de « Vidéos à la une » sur le portail' : 'La vidéo doit être active et visible sur le portail'}>
        Mettre à la une
      </button>
      {open && createPortal(
        <div role="dialog" aria-modal="true" aria-label="Mettre à la une"
             style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(0,0,0,0.55)' }}
             onClick={e => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div style={{ width: 'min(420px, 100%)', boxSizing: 'border-box', padding: 20, display: 'flex', flexDirection: 'column', gap: 12, borderRadius: 10,
                        color: 'var(--color-Panel-niv1-FG)', background: 'var(--color-Panel-niv1-BG)', border: '1px solid var(--color-Panel-niv1-Border)' }}>
            <strong style={{ fontSize: 17 }}>Mettre à la une</strong>
            <span style={{ overflowWrap: 'anywhere' }}>{v.name}</span>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              Durée
              <select value={duration} onChange={e => setDuration(e.target.value)} style={{ minHeight: 36 }}>
                {DURATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </label>
            {duration === 'date' &&
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                Fin de la mise à la une
                <input type="datetime-local" value={date} onChange={e => setDate(e.target.value)} style={{ minHeight: 36 }} />
              </label>}
            {error && <span role="alert" style={{ color: 'var(--color-NOK)' }}>{error}</span>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className={styles.secondary} onClick={() => setOpen(false)}>Annuler</button>
              <button type="button" className={styles.primary} disabled={busy} onClick={confirm}>Mettre à la une</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default VideoFeatureButton;
